import type { AuthMethod } from "@/config";

export type Locale = "en" | "pt-BR";

export interface Strings {
  logoAlt: string;
  pageTitleSr: string;
  welcomeBack: string;
  orDivider: string;
  secureAccess: string;
  sslSecured: string;
  encrypted: string;
  termsBefore: string;
  termsLink: string;
  termsBetween: string;
  privacyLink: string;
  copyright: (year: number) => string;
  googleNoToken: string;
  googleSignInFailed: string;
  // Google button locale (BCP-47 short tag accepted by the GSI client)
  googleButtonLocale: string;
}

const en: Strings = {
  logoAlt: "Vammo Logo",
  pageTitleSr: "Vammo - Sign In",
  welcomeBack: "Welcome Back",
  orDivider: "or",
  secureAccess: "Secure Access",
  sslSecured: "SSL Secured",
  encrypted: "Encrypted",
  termsBefore: "By signing in, you agree to our ",
  termsLink: "Terms of Service",
  termsBetween: " and ",
  privacyLink: "Privacy Policy",
  copyright: (year) => `© ${year} Vammo. All rights reserved.`,
  googleNoToken:
    "Authentication failed - no token received from Google. Please check your account and try again.",
  googleSignInFailed:
    "Sign-in failed. Please check your connection and try again.",
  googleButtonLocale: "en",
};

const ptBR: Strings = {
  logoAlt: "Logo Vammo",
  pageTitleSr: "Vammo - Entrar",
  welcomeBack: "Bem-vindo de volta",
  orDivider: "ou",
  secureAccess: "Acesso Seguro",
  sslSecured: "SSL",
  encrypted: "Criptografado",
  termsBefore: "Ao entrar, você concorda com nossos ",
  termsLink: "Termos de Serviço",
  termsBetween: " e ",
  privacyLink: "Política de Privacidade",
  copyright: (year) => `© ${year} Vammo. Todos os direitos reservados.`,
  googleNoToken:
    "Falha na autenticação — nenhum token recebido do Google. Verifique sua conta e tente novamente.",
  googleSignInFailed:
    "Falha ao entrar. Verifique sua conexão e tente novamente.",
  googleButtonLocale: "pt_BR",
};

const STRINGS: Record<Locale, Strings> = {
  en,
  "pt-BR": ptBR,
};

/**
 * Pick the locale for the auth-core shell from the auth methods the consumer
 * asked for. WhatsApp OTP is a rider-only flow whose copy is fixed in
 * Portuguese, so we use PT-BR whenever Google is not part of the experience.
 * As soon as Google is requested (alone or with WhatsApp), we render in
 * English to match the existing corporate flow.
 */
export const pickLocale = (authMethods: readonly AuthMethod[]): Locale =>
  authMethods.includes("google") ? "en" : "pt-BR";

export const stringsFor = (locale: Locale): Strings => STRINGS[locale];
